import {
  type ApproveCitationRequestInput,
  type ApproveCitationRequestResult,
  type CancelCitationRequestInput,
  type CitationRequest,
  CitationRequestCreateError,
  type CreateCitationRequestInput,
  type NotifyCitationRequestInput,
} from '../model/citationRequest';

import { fetchClient } from '@/util/fetchClient';

/**
 * Citation-request calls over the generated API client. Each returns the
 * wire value as the domain type and sends the domain input as the body,
 * so the compiler holds `model/citationRequest.ts` to the contract.
 */
export async function listCitationRequests(conversationId: string): Promise<CitationRequest[]> {
  const response = await fetchClient.GET('/v2/citation-requests/conversations/{conversationId}', {
    params: { path: { conversationId } },
  });

  return response.data?.data ?? [];
}

export async function createCitationRequest(
  input: CreateCitationRequestInput
): Promise<CitationRequest> {
  try {
    const response = await fetchClient.POST('/v2/citation-requests', { body: input });

    if (!response.data?.data) {
      throw new CitationRequestCreateError(
        'persist_failed',
        'Citation request create returned empty response'
      );
    }

    return response.data.data;
  } catch (error) {
    if (error instanceof CitationRequestCreateError) {
      throw error;
    }

    const fallback = error instanceof Error ? error.message : 'Unable to start a reference search.';

    throw new CitationRequestCreateError('persist_failed', fallback, error);
  }
}

/**
 * The backend owns the write: attached references
 * and help requests are reconciled server-side against
 * the supplied target state, and the returned `diff` reports what changed
 * for analytics.
 */
export async function approveCitationRequest(
  input: ApproveCitationRequestInput
): Promise<ApproveCitationRequestResult> {
  const response = await fetchClient.POST('/v2/citation-requests/{id}/approve', {
    params: { path: { id: input.citationRequestId } },
    body: {
      approvedReferenceIds: input.approvedReferenceIds,
      freeTextReferences: input.freeTextReferences,
      helpRequests: input.helpRequests,
    },
  });

  if (!response.data?.data || !response.data.diff) {
    throw new Error('Citation request approve returned empty response');
  }

  return { request: response.data.data, diff: response.data.diff };
}

export async function notifyCitationRequest(
  input: NotifyCitationRequestInput
): Promise<CitationRequest> {
  const response = await fetchClient.POST('/v2/citation-requests/{id}/notify', {
    params: { path: { id: input.citationRequestId } },
    body: { notifyWhenReady: input.notifyWhenReady },
  });

  if (!response.data?.data) {
    throw new Error('Citation request notify returned empty response');
  }

  return response.data.data;
}

export async function cancelCitationRequest(
  input: CancelCitationRequestInput
): Promise<CitationRequest> {
  const response = await fetchClient.POST('/v2/citation-requests/{id}/cancel', {
    params: { path: { id: input.citationRequestId } },
  });

  if (!response.data?.data) {
    throw new Error('Citation request cancel returned empty response');
  }

  return response.data.data;
}
